import React from 'react'
import type { QuoteFile } from 'paintscout'
import type { PresentationBlockProps } from '../..'
import { InlineEditableHtml, InlineEditableMedia } from '../../inlineEditable'
import { getBlockPadding, getPosterStyles, isMediaEmpty } from '../../util'
import compareBlockProps from '../../util/compareBlockProps'
import AnimationWrapper from '../../util/AnimationWrapper'
import classnames from 'classnames'
import { usePresentation } from '../../../context'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'

const useStyles = makeStyles<
  Theme,
  PresentationBlockProps & { useGlassEffect?: boolean; scrim?: string | null; customColor?: string }
>(
  (theme: Theme) => {
    return {
      root: {},
      // `customColor` is resolved by the component via `getPosterStyles` — a logo over an image
      // falls back to white even when the author never set a colour (#23099), the same way the
      // banner poster layout does.
      customColor: (props, color = props.customColor) => ({
        ...(color && {
          '& h1': {
            color: `${color} !important`
          },
          '& h2': {
            color: `${color} !important`
          },
          '& h3': {
            color: `${color} !important`
          },
          '& p': {
            color: `${color} !important`
          },
          '& ol': {
            color: `${color} !important`
          },
          '& ul': {
            color: `${color} !important`
          }
        })
      }),
      content: (props) => ({
        textShadow: props.block?.fields?.useContentBackground ? 'none' : `0 0 9px black`
      }),
      wrapper: {
        position: 'relative',
        overflow: 'hidden',
        padding: theme.spacing(10, 0)
      },
      imageWrapper: {
        top: 0,
        left: 0,
        position: 'absolute',
        height: '100%',
        width: '100%',
        overflow: 'hidden'
      },
      // Flat fill across the whole image rather than a bottom-edge gradient — a scraped hero can
      // carry its own headline and logo anywhere in the frame (#23099). Painted between the image
      // wrapper and `contentWrapper` on DOM order alone — `contentWrapper`'s own `zIndex: 1` keeps
      // the text above it — and never eats a click meant for the image.
      imageScrim: (props) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        backgroundColor: props.scrim ?? 'transparent',
        pointerEvents: 'none'
      }),
      contentWrapper: (props) => ({
        textAlign: 'center',
        padding: getBlockPadding({ block: props.block, source: 'block', size: 'desktop', contentWidth: 'md' }),
        position: 'relative',
        zIndex: 1,
        [theme.breakpoints.down('md')]: {
          padding: getBlockPadding({ block: props.block, source: 'block', size: 'tablet', contentWidth: 'md' })
        },
        [theme.breakpoints.down('sm')]: {
          padding: getBlockPadding({ block: props.block, source: 'block', size: 'mobile', contentWidth: 'md' })
        }
      }),
      contentSubwrapper: (
        props,
        useContentBackground = props.block?.fields?.useContentBackground,
        contentBackgroundColor = props.block?.fields?.contentBackgroundColor,
        useGlassEffect = props.useGlassEffect && CSS.supports('backdrop-filter', 'blur(4px)')
      ) => ({
        textAlign: 'left',
        transition: 'all .3s ease',
        ...(useContentBackground && {
          backgroundColor: useGlassEffect ? 'rgba(255, 255, 255, .25)' : contentBackgroundColor,
          backdropFilter: useGlassEffect ? 'blur(5px) saturate(80%) contrast(80%) brightness(130%)' : '',
          padding: theme.spacing(8),
          display: 'inline-block',
          margin: '0 auto'
        }),
        [theme.breakpoints.down('md')]: {
          padding: theme.spacing(4)
        },
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(2)
        }
      }),
      fullSize: {
        height: '100%',
        width: '100%'
      },
      placeholder: (props, isEditable = props.isEditable) => ({
        display: isEditable ? 'flex' : 'none',
        height: 75,
        margin: theme.spacing(1, 0)
      }),
      logoWrapper: (props, logoAlignment = props.block?.fields?.logoAlignment) => ({
        textAlign: logoAlignment,
        width: '100%'
      }),
      logo: (props, logoSize = props.block?.fields?.logoSize) => ({
        ...(logoSize === 'lg' && {
          maxHeight: 300,
          maxWidth: 400,
          objectFit: 'cover'
        }),
        ...(logoSize === 'md' && {
          maxHeight: 200,
          maxWidth: 300,
          objectFit: 'cover'
        }),
        ...(logoSize === 'sm' && {
          maxHeight: 100,
          maxWidth: 200,
          objectFit: 'cover'
        })
      })
    }
  },
  { name: 'LogoBlock' }
)

interface LogoSectionProps extends PresentationBlockProps {
  logo: string
}

function LogoBlock(props: LogoSectionProps) {
  const {
    isEditable,
    isSettings,
    logo,
    presetColors,
    block,
    blockPrefix,
    setFieldValue,
    submitForm,
    WrapperComponent
  } = props

  const useGlassEffect = block?.fields?.useGlassEffect && CSS.supports('backdrop-filter', 'blur(4px)')
  const { animation, content, contentTop, image, useContentBackground, color, overlay, contentBackgroundColor } =
    block.fields

  // A content-background panel is authored text over a color the author picked, so any text color
  // is an RTE-level concern (set per rich-text field, same as every other block) rather than a
  // block-wide override — there's no `posterTextColor` fallback to apply in that case. The scrim
  // and forced fallback color below are only for the raw-image case (#23099's acceptance criteria).
  const { textColor: posterTextColor, scrim } = getPosterStyles({
    hasImage: !isMediaEmpty({ file: image }) && !useContentBackground,
    color,
    overlay
  })
  const customColor = useContentBackground ? undefined : posterTextColor

  const classes = useStyles({ useGlassEffect, scrim, customColor, ...props })

  const isAnimated = ['slide', 'fade', 'zoom'].includes(animation)

  const { presentationLibrary } = usePresentation()

  const handleEditField = (field: string, content: string | QuoteFile) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  return (
    <WrapperComponent {...props}>
      <div className={classes.wrapper}>
        <AnimationWrapper animation={animation} index={0} direction={'right'} className={classes.imageWrapper}>
          <InlineEditableMedia
            mediaOptionsProps={{
              blockPrefix: blockPrefix,
              fieldSuffix: 'image'
            }}
            accept={'image/*'}
            classes={{ root: classes.fullSize, image: classes.fullSize, size: classes.fullSize }}
            file={image}
            getFocusFromSection={true}
            handleEdit={(file) => handleEditField('fields.image', file)}
            isAnimated={isAnimated}
            isEditable={isEditable}
            isSettings={isSettings}
            placeholderButtonText={'Add Image'}
            presentationLibrary={presentationLibrary}
            youtube={false}
          />
        </AnimationWrapper>
        {scrim && <div className={classes.imageScrim} />}

        <div className={classes.contentWrapper}>
          <AnimationWrapper animation={animation} index={1} direction={'bottom'}>
            <div
              className={classnames(classes.contentSubwrapper, {
                ['themeBackground']: useContentBackground && !useGlassEffect && !contentBackgroundColor
              })}
            >
              <InlineEditableHtml
                classes={{
                  root: classnames({ [classes.content]: true, [classes.customColor]: !!customColor }),
                  placeholder: classes.placeholder
                }}
                isEditable={isEditable}
                isSettings={isSettings}
                handleEdit={(content: string) => handleEditField('fields.contentTop', content)}
                content={contentTop}
                placeholderButtonText={'Add Text (Optional)'}
                presetColors={presetColors}
                fieldKey={`${blockPrefix}.fields.contentTop`}
              />
              <div className={classes.logoWrapper}>
                <img className={classes.logo} src={logo} />
              </div>
              <InlineEditableHtml
                classes={{
                  root: classnames({ [classes.content]: true, [classes.customColor]: !!customColor }),
                  placeholder: classes.placeholder
                }}
                isEditable={isEditable}
                isSettings={isSettings}
                handleEdit={(content: string) => handleEditField('fields.content', content)}
                content={content}
                placeholderButtonText={'Add Text (Optional)'}
                presetColors={presetColors}
                fieldKey={`${blockPrefix}.fields.content`}
              />
            </div>
          </AnimationWrapper>
        </div>
      </div>
    </WrapperComponent>
  )
}

export default React.memo(LogoBlock, (prevProps, nextProps) => {
  if (prevProps.logo !== nextProps.logo) return false
  return compareBlockProps(prevProps, nextProps)
})
